import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  List,
  Typography,
  Table,
  Spin,
  message,
  Space,
  Button,
  Tooltip,
} from "antd";
import {
  TeamOutlined,
  ApartmentOutlined,
  ClockCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import axios from "axios";
import config from "../../config";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const { Text } = Typography;

const AttendanceSummary = ({ month }) => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch positions for department mapping
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const { data } = await api.get(`/positions`);
        setUserRoles(data);
      } catch (error) {
        console.error("Failed to fetch positions", error);
      }
    };
    fetchUserRoles();
  }, [apiUrl]);

  // Fetch summary when month changes
  useEffect(() => {
    if (!month) return;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/hr/attendance/summary?month=${month}`,
        );
        setSummary(data);
      } catch (error) {
        message.error("Failed to load attendance summary");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [month, apiUrl]);

  if (loading) return <Spin tip="Loading monthly summary..." />;
  if (!summary.length) return <div>No attendance data for this month.</div>;

  const totalEmployees = summary.length;
  const presentCount = summary.filter((s) => s.days_present > 0).length;
  const attendanceRate =
    totalEmployees > 0 ? Math.round((presentCount / totalEmployees) * 100) : 0;
  const totalHours = summary
    .reduce((sum, curr) => sum + parseFloat(curr.total_hours), 0)
    .toFixed(2);

  // Department-wise aggregation
  const departmentData = Object.entries(
    summary.reduce((acc, curr) => {
      const dept = curr.department || "Unknown";
      if (!acc[dept]) acc[dept] = { count: 0, hours: 0, employees: 0 };
      acc[dept].count += curr.days_present;
      acc[dept].hours += parseFloat(curr.total_hours);
      acc[dept].employees++;
      return acc;
    }, {}),
  )
    .map(([department, data]) => ({
      department,
      avg_days_present: (data.count / data.employees).toFixed(1),
      total_hours: data.hours.toFixed(1),
    }))
    .sort((a, b) => b.avg_days_present - a.avg_days_present);

  // Helper to get department name from department_id
  const getDepartmentName = (deptId) => {
    const role = userRoles.find(
      (r) => Number(r.department_id) === Number(deptId),
    );
    return role?.department_name || "—";
  };

  // Table columns for monthly summary
  const columns = [
    {
      title: "Employee Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (deptId) => getDepartmentName(deptId),
      sorter: (a, b) =>
        getDepartmentName(a.department).localeCompare(
          getDepartmentName(b.department),
        ),
    },
    {
      title: "Days Present",
      dataIndex: "days_present",
      key: "days_present",
      sorter: (a, b) => a.days_present - b.days_present,
    },
    {
      title: "Total Hours",
      dataIndex: "total_hours",
      key: "total_hours",
      render: (hours) => parseFloat(hours).toFixed(2),
      sorter: (a, b) => parseFloat(a.total_hours) - parseFloat(b.total_hours),
    },
  ];

  const exportMonthlyCSV = () => {
    const headers = [
      "Employee Name",
      "Department",
      "Days Present",
      "Total Hours",
    ];
    const rows = summary.map((row) => [
      row.name,
      getDepartmentName(row.department),
      row.days_present,
      parseFloat(row.total_hours).toFixed(2),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, `attendance_summary_${month}.csv`);
    message.success("CSV exported");
  };

  const exportMonthlyPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Employee Name", "Department", "Days Present", "Total Hours"]],
      body: summary.map((row) => [
        row.name,
        getDepartmentName(row.department),
        row.days_present,
        parseFloat(row.total_hours).toFixed(2),
      ]),
    });
    doc.save(`attendance_summary_${month}.pdf`);
    message.success("PDF exported");
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Attendance Rate */}
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <Statistic
              title={
                <>
                  <TeamOutlined style={{ marginRight: 8, color: "#1677ff" }} />
                  Attendance Rate
                </>
              }
              value={attendanceRate}
              suffix="%"
              valueStyle={{ color: "#1677ff", fontWeight: "bold" }}
            />
            <Text type="secondary">
              {presentCount} of {totalEmployees} employees present
            </Text>
          </Card>
        </Col>

        {/* Top Departments */}
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <div style={{ marginBottom: 8 }}>
              <ApartmentOutlined style={{ marginRight: 8, color: "#722ed1" }} />
              <strong>Top Departments</strong>
            </div>
            <List
              size="small"
              dataSource={departmentData.slice(0, 3)}
              renderItem={(dept) => (
                <List.Item className="flex justify-between text-sm">
                  <span>{dept.department}</span>
                  <Text strong>{dept.avg_days_present} days avg</Text>
                </List.Item>
              )}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing top 3 departments
            </Text>
          </Card>
        </Col>

        {/* Hours Worked */}
        <Col xs={24} md={8}>
          <Card bordered={false}>
            <Statistic
              title={
                <>
                  <ClockCircleOutlined
                    style={{ marginRight: 8, color: "#52c41a" }}
                  />
                  Hours Worked
                </>
              }
              value={totalHours}
              precision={2}
              valueStyle={{ color: "#52c41a", fontWeight: "bold" }}
            />
            <Text type="secondary">Total hours this month</Text>
          </Card>
        </Col>
      </Row>
      {/* Employee Attendance Summary Table */}
      <Card
        title="Employee Attendance Summary"
        extra={
          <Space>
            <Tooltip title="Export CSV">
              <Button icon={<FileExcelOutlined />} onClick={exportMonthlyCSV}>
                CSV
              </Button>
            </Tooltip>
            <Tooltip title="Export PDF">
              <Button icon={<FilePdfOutlined />} onClick={exportMonthlyPDF}>
                PDF
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Table
          size="small"
          columns={columns}
          dataSource={summary}
          rowKey={(record) => record.user_id || record.id}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          loading={loading}
          scroll={{ x: "max-content" }}
        />
      </Card>
    </>
  );
};

export default AttendanceSummary;
